import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Building2, Upload, X as XIcon, Image as ImageIcon, Sparkles } from "lucide-react";
import { COMMERCE_ICON_PRESETS, getCommerceIcon, isCustomCommerceIcon, isCommerceIconAccented } from "../lib/commerceIcons";

/**
 * Establishment icon picker (preset OR custom upload). The selection is
 * stored on the commerce document under `icon` and rendered next to the
 * establishment name on the dashboard + public display.
 */
export default function EstablishmentIconPanel({ commerce, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const icon = commerce?.icon || "";
  const PresetIcon = getCommerceIcon(icon);
  const customDataUrl = isCustomCommerceIcon(icon) ? icon : null;

  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/merchant/me/icon", { icon: next || "" });
      toast.success("Icône Établissement mise à jour");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const handleUpload = (e) => {
    setUploadErr("");
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|svg\+xml)$/.test(file.type)) {
      setUploadErr("Format non supporté (PNG, JPG ou SVG uniquement)");
      return;
    }
    if (file.size > 1_500_000) {
      setUploadErr("Image trop volumineuse (1,5 Mo max)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = String(reader.result || "");
      try {
        if (file.type === "image/svg+xml") {
          await save(raw);
          return;
        }
        const img = new Image();
        img.onload = async () => {
          const TARGET = 160;
          const canvas = document.createElement("canvas");
          canvas.width = TARGET; canvas.height = TARGET;
          const ctx = canvas.getContext("2d");
          const ratio = Math.min(TARGET / img.width, TARGET / img.height);
          const w = img.width * ratio, h = img.height * ratio;
          ctx.clearRect(0, 0, TARGET, TARGET);
          ctx.drawImage(img, (TARGET - w) / 2, (TARGET - h) / 2, w, h);
          await save(canvas.toDataURL("image/png", 0.9));
        };
        img.onerror = () => setUploadErr("Image illisible");
        img.src = raw;
      } catch { setUploadErr("Erreur d'import"); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-1.5 h-10 px-2 rounded-lg border border-border bg-white text-ink hover:border-brand hover:bg-brand/5 hover:text-brand transition-colors shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
        data-testid="commerce-icon-open-btn"
        title="Choisir l'icône de l'Établissement"
        aria-label="Choisir l'icône de l'Établissement"
      >
        <Sparkles size={16} className="text-brand" />
        <span className="text-[11px] font-bold uppercase tracking-wider">Icône</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="commerce-icon-modal"
        >
          <div
            className="relative w-full max-w-5xl bg-white rounded-2xl border border-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h2 className="font-display font-black uppercase tracking-[0.06em] text-base text-ink inline-flex items-center gap-2">
                <ImageIcon size={16} className="text-brand" /> Icône Établissement
              </h2>
              <button onClick={() => setOpen(false)} className="text-subtle hover:text-ink" aria-label="Fermer" data-testid="commerce-icon-close">
                <XIcon size={18} />
              </button>
            </header>
            <div className="p-5 max-h-[75vh] overflow-y-auto">
              <p className="text-xs text-subtle leading-relaxed">
                Choisissez une icône qui représente votre Établissement. Elle s'affichera sur le tableau de bord et l'écran public.
              </p>
              <div
                className="mt-4 grid gap-2 sm:gap-2.5 grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-10"
                data-testid="commerce-icon-presets"
              >
                {COMMERCE_ICON_PRESETS.map(({ key, label, Icon, accent }) => {
                  const active = icon === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => save(key)}
                      disabled={saving}
                      title={label}
                      className={`group flex flex-col items-center justify-start gap-1.5 p-2 rounded-xl border transition-colors min-h-[88px] ${
                        active
                          ? "border-brand bg-brand/10 text-brand ring-2 ring-brand/30"
                          : "border-[#E1E8F5] bg-[#F2F6FF] hover:border-brand hover:bg-white"
                      }`}
                      data-testid={`commerce-icon-${key}`}
                    >
                      <span className="flex items-center justify-center w-10 h-10 shrink-0">
                        <Icon
                          size={28}
                          strokeWidth={1.8}
                          className={active ? "text-brand" : accent ? "text-brand" : "text-ink"}
                        />
                      </span>
                      <span className={`text-[10px] leading-tight text-center font-bold ${active ? "text-brand" : "text-ink"}`}>
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border-2 border-ink text-ink hover:bg-ink hover:text-white text-xs font-bold uppercase tracking-wider cursor-pointer" data-testid="commerce-icon-upload-label">
                  <Upload size={14} /> Importer une icône
                  <input
                    type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleUpload} className="sr-only" disabled={saving}
                    data-testid="commerce-icon-upload-input"
                  />
                </label>
                {customDataUrl ? (
                  <div className="inline-flex items-center gap-2">
                    <img src={customDataUrl} alt="" className="w-10 h-10 rounded border border-border bg-white object-contain" />
                    <button
                      onClick={() => save("")}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border text-subtle hover:border-ink hover:text-ink text-[11px] uppercase font-bold"
                      data-testid="commerce-icon-clear"
                    >
                      <XIcon size={12} /> Retirer
                    </button>
                  </div>
                ) : PresetIcon ? (
                  <button
                    onClick={() => save("")}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border text-subtle hover:border-ink hover:text-ink text-[11px] uppercase font-bold"
                    data-testid="commerce-icon-clear"
                  >
                    <XIcon size={12} /> Retirer l'icône
                  </button>
                ) : null}
                {uploadErr && (
                  <p className="basis-full text-xs text-destructive" data-testid="commerce-icon-upload-error">{uploadErr}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
